import React, { useRef } from "react";
import {
  Box,
  Divider,
  Grid,
  Portal,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useFocusTrap, focusVisibleSx, onEnterSpace } from "@3sc/common-component";

import images from "../../constant/images";

const SideDrawer = ({
  onCloseClick,
  isDrawerOpen,
  drawerTitle,
  children,
  styles,
  expanded,
  setExpanded,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const filter = isDark
    ? "invert(100%) sepia(0%) saturate(0%) hue-rotate(93deg) brightness(103%) contrast(103%)"
    : "invert(48%) sepia(0%) saturate(0%) hue-rotate(181deg) brightness(95%) contrast(90%)";

  const panelRef = useRef(null);
  useFocusTrap(panelRef, {
    enabled: isDrawerOpen,
    onEscape: onCloseClick,
    autoFocus: true,
    restoreFocus: true,
  });

  return (
    <>
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        <Grid container sx={{ height: "100%" }}>
          {isDrawerOpen && (
            <Box
              onClick={onCloseClick}
              sx={{
                position: "fixed",
                top: "7rem",
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: "#07080B80",
                backdropFilter: "blur(3px)",
                opacity: 1,
                transition: "opacity 0.3s ease-in-out",
                zIndex: 3,
              }}
            />
          )}

          <Grid
            item
            ref={panelRef}
            inert={!isDrawerOpen || undefined}
            role={isDrawerOpen ? "dialog" : undefined}
            aria-modal={isDrawerOpen || undefined}
            aria-label={isDrawerOpen ? drawerTitle : undefined}
            sx={{
              width: expanded ? "90%" : styles?.drawerWidth || "25%",
              position: "fixed",
              top: "7rem",
              right: 0,
              height: "calc(100vh - 7rem)",
              backgroundColor: theme.palette.primary.background,
              transform: isDrawerOpen ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.35s cubic-bezier(0.4,0,0.2,1)",
              zIndex: 4,
              borderLeft: `0.1rem solid ${theme.palette.primary.borderColor}`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header Section */}
            <Stack
              direction={"row"}
              alignItems={"center"}
              sx={{
                width: "100%",
                flexShrink: 0,
                paddingBottom: "0.8rem",
                px: 2,
                pt: "0.8rem",
                borderBottom: (theme) =>
                  `0.1rem solid ${theme.palette.primary.borderColor}`,
              }}
            >
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                flexGrow={1}
              >
                <Stack
                  direction={"row"}
                  gap={"0.5rem"}
                  alignItems={"center"}
                  sx={{
                    cursor: "pointer",
                  }}
                >
                  <Box
                    onClick={onCloseClick}
                    onKeyDown={onEnterSpace(onCloseClick)}
                    role="button"
                    tabIndex={0}
                    aria-label="Close"
                    sx={focusVisibleSx(theme)}
                  >
                    <img
                      src={images.Cross}
                      style={{
                        filter,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "1.2rem",
                      color: theme.palette.primary.textColor,
                    }}
                  >
                    {drawerTitle}
                  </Typography>
                </Stack>
                <Stack direction={"row"} gap={"0.5rem"} alignItems={"center"}>
                  <Box
                    onClick={() => setExpanded((prev) => !prev)}
                    onKeyDown={onEnterSpace(() => setExpanded((prev) => !prev))}
                    role="button"
                    tabIndex={0}
                    aria-label={expanded ? "Collapse" : "Expand"}
                    sx={{ cursor: "pointer", ...focusVisibleSx(theme) }}
                  >
                    <Box
                      component="img"
                      src={expanded ? images.Collapse : images.Expand}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Stack>

            {/* Scrollable Content */}
            <Box
              sx={{
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                overflow: "auto",
                px: 2,
              }}
            >
              {children}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default SideDrawer;